// /babies — pick which tracker to open.
//
// Shown whenever the account has more than one baby and we can't be sure which
// one is meant, and reachable any time from the Today menu ("Switch baby"). The
// entry count and last-entry time are deliberate: someone who just signed in
// and landed on the wrong baby needs to see, without reading anything, that
// their weeks of data are sitting right there on the other row.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabaseReady } from "../lib/supabase";
import { useAuth } from "../lib/AuthContext";
import { lastUsedBabyId } from "../lib/babyId";
import { listMyBabies, type MyBaby } from "../lib/myBabies";
import { ageFromBirthday, formatAge } from "../lib/age";
import { BabyAvatar } from "../components/BabyAvatar";
import { useT } from "../lib/LocaleContext";

export function BabyPicker() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const t = useT();
  const [babies, setBabies] = useState<MyBaby[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!supabaseReady) {
      setError(t.babyShell.storageError);
      return;
    }
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await listMyBabies(user.id);
        if (!cancelled) setBabies(rows);
      } catch {
        if (!cancelled) setError(t.common.somethingWrong);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  function open(id: string) {
    lastUsedBabyId.set(user?.id, id);
    navigate(`/b/${id}`);
  }

  if (error) {
    return (
      <main className="screen screen--center">
        <h1>{t.babyPicker.title}</h1>
        <p className="error" role="alert">{error}</p>
      </main>
    );
  }

  if (babies === null) {
    return (
      <main className="screen screen--center">
        <p className="muted">{t.common.loading}</p>
      </main>
    );
  }

  return (
    <div className="bh-root">
      <div style={styles.head}>
        <div style={styles.title}>{t.babyPicker.title}</div>
        <p style={styles.sub}>{t.babyPicker.subtitle}</p>
      </div>

      <div className="bh-scroll" style={{ paddingBottom: 40 }}>
        <div style={styles.list}>
          {babies.map((b) => (
            <button key={b.id} style={styles.card} onClick={() => open(b.id)}>
              <BabyAvatar size={52} name={b.name} photoDataUrl={b.photo_data} />
              <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
                <div style={styles.name}>{b.name}</div>
                <div style={styles.meta}>
                  {formatAge(ageFromBirthday(b.due_date ?? b.birthday))}
                  {" · "}
                  {b.role === "creator" ? t.settings.roleCreator : t.settings.roleCaretaker}
                </div>
                <div style={b.entryCount === 0 ? styles.activityEmpty : styles.activity}>
                  {b.entryCount === 0
                    ? t.babyPicker.noEntries
                    : t.babyPicker.activity(b.entryCount, relativeDay(b.lastEntryAt, t))}
                </div>
              </div>
              <span style={styles.chevron} aria-hidden="true">›</span>
            </button>
          ))}
        </div>

        <button style={styles.addBtn} onClick={() => navigate("/new")}>
          {t.babyPicker.addBaby}
        </button>
      </div>
    </div>
  );
}

// Coarse on purpose — "3 days ago" is all it takes to spot the abandoned baby.
function relativeDay(iso: string | null, t: ReturnType<typeof useT>): string {
  if (!iso) return t.babyPicker.never;
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 60) return t.babyPicker.minutesAgo(mins);
  const hours = Math.round(mins / 60);
  if (hours < 36) return t.babyPicker.hoursAgo(hours);
  return t.babyPicker.daysAgo(Math.round(hours / 24));
}

const styles: Record<string, React.CSSProperties> = {
  head: { padding: "24px 20px 12px" },
  title: {
    fontFamily: "var(--bh-font-serif)",
    fontSize: 28,
    fontWeight: 500,
    color: "var(--bh-ink)",
    letterSpacing: "-0.02em",
  },
  sub: { fontSize: 15, color: "var(--bh-ink-3)", margin: "8px 0 0", lineHeight: 1.5 },
  list: { display: "flex", flexDirection: "column", gap: 10, padding: "8px 14px" },
  card: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    appearance: "none",
    background: "var(--bh-surface)",
    border: "1px solid var(--bh-divider)",
    borderRadius: 20,
    padding: "16px 18px",
    cursor: "pointer",
    fontFamily: "inherit",
    minHeight: 84,
  },
  name: { fontSize: 19, fontWeight: 600, color: "var(--bh-ink)" },
  meta: { fontSize: 13, color: "var(--bh-ink-3)", marginTop: 2 },
  activity: { fontSize: 13, color: "var(--bh-ink-2)", marginTop: 4 },
  activityEmpty: { fontSize: 13, color: "var(--bh-ink-4)", marginTop: 4 },
  chevron: { fontSize: 22, color: "var(--bh-ink-4)" },
  addBtn: {
    width: "calc(100% - 28px)",
    margin: "14px 14px 0",
    appearance: "none",
    background: "transparent",
    border: "1px dashed var(--bh-divider)",
    borderRadius: 20,
    padding: "16px",
    fontSize: 15,
    fontFamily: "inherit",
    color: "var(--bh-ink-3)",
    cursor: "pointer",
    minHeight: 56,
  },
};
